import countries from 'i18n-iso-countries';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';

import { MSG_AUTH_EXPIRE } from '../../../constants/constants';
import {
  trackSlsWarehouseCreated,
  trackSlsWarehouseUpdated,
  trackSlsUpdateFailed,
  trackSlsSearchPerformed,
} from '../../../utils/analytics';
import { parseSLSandBrickworksErrors } from '../../../utils/formatting';
import { postWarehouse, putWarehouse } from '../../../utils/service-calls/sls';
import { getFilteredWarehouses } from '../../../utils/sls-filter-handlers';
import { countryRegionMap } from '../../../utils/static/country-mappings';
import { isUserAllowed, isUserReadOnly } from '../../../utils/tab-permissions';
import { Main } from '../generic';

import WarehousesForm from './WarehousesForm';

const config = require('../../../config')();
// have to do this to get country names in english
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

class WarehousesMain extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      country: { label: 'All Countries', value: '' },
      formOpen: this.props.detailOnly || false,
      postError: '',
      posting: false,
      postSuccess: '',
      putError: '',
      putSuccess: '',
      putting: false,
      region: { label: 'All Regions', value: '' },
      search: '',
      state: { label: 'All States', value: '' },
    };
  }

  clearFilters = () => this.setState({
    country: { label: 'All Countries', value: '' },
    region: { label: 'All Regions', value: '' },
    search: '',
    state: { label: 'All States', value: '' },
  }, () => this.props.updateWarehouses(getFilteredWarehouses(this.state, this.props.allWarehouses)));

  closeOrOpenForm = (formData = {}) => {
    this.props.updateFormData(formData);
    if (!this.state.formOpen) {
      // if the form is currently closed, open it
      this.setState({
        formOpen: true, postError: '', postSuccess: '', putError: '', putSuccess: '',
      });
    } else {
      // if the form is currently open, close it
      this.setState({ formOpen: false });
    }
    this.props.history.push('/storelocationservices/warehouses', {
      postError: '', postSuccess: '', putError: '', putSuccess: '',
    });
  }

  handleError = (method, message) => this.setState({ formOpen: false, [method]: message },
    () => this.handleFetch());

  handleFetch = () => this.setState({ posting: false, putting: false, search: '' },
    () => this.props.history.push('/storelocationservices/warehouses', this.state));

  handleSuccess = (method, message) => this.setState({ formOpen: false, [method]: message },
    () => this.handleFetch());

  post = (data) => this.setState({ posting: true },
    async () => postWarehouse(this.props.userInfo.accessToken, data)
      .then((response) => {
        // Track warehouse creation success
        trackSlsWarehouseCreated({
          region: countryRegionMap[data.address?.country],
          userEmail: this.props.userInfo.email,
          warehouseId: response?.body?.id || data.id,
          warehouseNumber: data.name,
        });
        return this.handleSuccess('postSuccess', 'Successfully added Warehouse.');
      })
      .catch((error) => {
        const err = parseSLSandBrickworksErrors(error);

        // Track warehouse creation failure
        trackSlsUpdateFailed({
          errorCode: err.statusCode,
          errorMessage: err.message,
          errorType: err.timeout ? 'timeout' : 'api',
          facilityType: 'warehouses',
          storeId: data.name,
          userEmail: this.props.userInfo.email,
        });

        if (err.timeout) return this.handleError('postError', 'POST timed out. Please refresh and try again.');
        if (err.message === MSG_AUTH_EXPIRE) {
          // don't refresh the page if the reason their call failed was because of old auth. they need to see the error first.
          return this.setState({ postError: `POST failed with error: ${err.message}`, posting: false });
        }
        return this.handleError('postError', `POST failed with error: ${err.message}`);
      }));

  put = (data) => this.setState({ putting: true },
    async () => {
      const dataToSend = data;
      if (!isUserAllowed(this.props.userInfo.groups, 'SLS.IP')
        || countryRegionMap[(this.props.formData.address ? this.props.formData.address.country : '')] !== 'EUROPE_MIDDLE_EAST_AFRICA') {
        // if we are not editing IP attributes, don't send them in the update
        delete dataToSend.islandPacificAttributes;
      }
      return putWarehouse(this.props.userInfo.accessToken, { new: dataToSend, old: this.props.formData })
        .then(() => {
          // Track warehouse update success
          trackSlsWarehouseUpdated({
            userEmail: this.props.userInfo.email,
            warehouseId: data.id,
            warehouseNumber: data.name,
          });
          return this.handleSuccess('putSuccess', 'Successfully updated Warehouse.');
        })
        .catch((error) => {
          const err = parseSLSandBrickworksErrors(error);

          // Track warehouse update failure
          trackSlsUpdateFailed({
            errorCode: err.statusCode,
            errorMessage: err.message,
            errorType: err.timeout ? 'timeout' : 'api',
            facilityType: 'warehouses',
            storeId: data.id,
            userEmail: this.props.userInfo.email,
          });

          if (err.timeout) return this.handleError('putError', 'PUT timed out. Please refresh and try again.');
          if (err.message === MSG_AUTH_EXPIRE) {
            // don't refresh the page if the reason their call failed was because of old auth. they need to see the error first.
            return this.setState({ postError: `POST failed with error: ${err.message}`, posting: false });
          }
          return this.handleError('putError', `PUT failed with error: Error: ${err.message}`);
        });
    });

  updateFilter = (filterType, value) => this.setState({ [filterType]: { label: value.label, value: value.value } },
    () => this.props.updateWarehouses(getFilteredWarehouses(this.state, this.props.allWarehouses)));

  updateSearch = (search) => this.setState({ search },
    () => {
      const filteredWarehouses = getFilteredWarehouses(this.state, this.props.allWarehouses);
      this.props.updateWarehouses(filteredWarehouses);

      // Track search if search term is meaningful (3+ characters)
      if (search && search.length >= 3) {
        trackSlsSearchPerformed({
          facilityType: 'warehouses',
          resultsCount: filteredWarehouses?.length || 0,
          searchTerm: search,
        });
      }
    });

  render = () => (
    <Main
      ccmConfig={this.props.ccmConfig}
      changeSearch={(value) => this.updateSearch(value)}
      clearFilters={this.clearFilters}
      closeOrOpenForm={(data) => this.closeOrOpenForm(data)}
      columns={[
        {
          accessor: (d) => (d.name),
          Cell: (d) => <a href={`${config.host}/storelocationservices/warehouses/detail/${d.original.id}`} style={{ display: 'block' }}>{d.original.name}</a>,
          Header: 'Warehouse Name',
          id: 'Warehouse Name',
          style: { fontWeight: 'bold' },
        },
        {
          accessor: (d) => (d.company),
          Cell: (d) => <a href={`${config.host}/storelocationservices/warehouses/detail/${d.original.id}`} style={{ display: 'block' }}>{d.original.company}</a>,
          Header: 'Company',
          id: 'Company',
          width: 200,
        },
        {
          accessor: (d) => (`${d.address.city}${d.address.state ? `, ${d.address.state}` : ''}`),
          Cell: (d) => <a href={`${config.host}/storelocationservices/warehouses/detail/${d.original.id}`} style={{ display: 'block' }}>{`${d.original.address.city}${d.original.address.state ? `, ${d.original.address.state}` : ''}`}</a>,
          Header: 'City, (State/Province)',
          id: 'City, (State/Province)',
        },
        {
          accessor: (d) => (d.address.country),
          Cell: (d) => <a href={`${config.host}/storelocationservices/warehouses/detail/${d.original.id}`} style={{ display: 'block' }}>{d.original.address.country}</a>,
          Header: 'Country',
          id: 'Country',
          width: 200,
        },
      ]}
      country={this.state.country}
      facilities={this.props.warehouses}
      facilitiesAll={this.props.allWarehouses}
      facilityType="Warehouses"
      filters={[
        {
          accessor: 'region',
          changeValue: (value) => this.updateFilter('region', value),
          getLabel: (code) => code,
          id: 'regionFilter',
          label: 'All Regions',
          option: this.state.region,
        },
        {
          accessor: 'address.country',
          changeValue: (value) => this.updateFilter('country', value),
          // TODO: remove Türkiye hardcoding when dependent module releases a version with the new name
          getLabel: (code) => (code === 'TUR' ? 'Türkiye' : countries.getName(code, 'en')),
          id: 'countryFilter',
          label: 'All Countries',
          option: this.state.country,
        },
        {
          accessor: 'address.state',
          changeValue: (value) => this.updateFilter('state', value),
          getLabel: (code) => code,
          id: 'stateFilter',
          label: 'All States',
          option: this.state.state,
        },
      ]}
      form={(
        <WarehousesForm
          adding={this.props.formData.id === undefined}
          ccmConfig={this.props.ccmConfig}
          formData={this.props.formData}
          showIP={isUserAllowed(this.props.userInfo.groups, 'SLS.IP')
            && countryRegionMap[(this.props.formData.address ? this.props.formData.address.country : '')] === 'EUROPE_MIDDLE_EAST_AFRICA'}
          submit={this.props.formData.id === undefined
            ? (data, errs) => this.post(data, errs)
            : (data, errs) => this.put(data, errs)}
          userGroups={this.props.userInfo.groups}
          userIsReadOnly={isUserReadOnly(this.props.userInfo.groups, ['SLS.ReadOnly', 'SLS'])}
        />
      )}
      formOpen={this.state.formOpen}
      getError={this.props.getError}
      getting={this.props.getting}
      post={(data) => this.submit(data)}
      postError={this.props.history.location.state?.postError || this.state.postError || ''}
      postSuccess={this.props.history.location.state?.postSuccess || ''}
      posting={this.state.posting}
      put={(data) => this.submit(data)}
      putError={this.props.history.location.state?.putError || this.state.putError || ''}
      putSuccess={this.props.history.location.state?.putSuccess || ''}
      putting={this.state.putting}
      region={this.state.region}
      search={this.state.search}
      userGroups={this.props.userInfo.groups}
    />
  );
}

WarehousesMain.propTypes = {
  allWarehouses: PropTypes.arrayOf(PropTypes.shape).isRequired,
  ccmConfig: PropTypes.shape().isRequired,
  detailOnly: PropTypes.bool.isRequired,
  formData: PropTypes.shape({
    address: PropTypes.shape({
      country: PropTypes.string,
    }),
    id: PropTypes.string,
  }).isRequired,
  getError: PropTypes.string.isRequired,
  getting: PropTypes.bool.isRequired,
  history: PropTypes.shape().isRequired,
  updateFormData: PropTypes.func.isRequired,
  updateWarehouses: PropTypes.func.isRequired,
  userInfo: PropTypes.shape({
    accessToken: PropTypes.string,
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  warehouses: PropTypes.arrayOf(PropTypes.shape).isRequired,
};

export default withRouter(WarehousesMain);
